import React, { useEffect, useRef, useState } from "react";
import { Button, Alert, Modal } from "@repo/ui";
import { GiReceiveMoney } from "react-icons/gi";
import { FaGift } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useAppDispatch";
import { setEvent } from "../../../../../redux/slices/eventBusSlice";
import { fetchGiftTableStatus } from "../../../../../redux/slices/giftTableSlice";
import {
  setCompletedWithdrawTotal,
  setPendingReviewWithdrawTotal,
  setHasPendingReview,
} from "../../../../../redux/slices/withdrawalsSlice";
import { RootState } from "../../../../../redux/store";
import { generateGetURL } from "../../../../../services/mediaApiService";
import {
  getSuccessfulWithdrawalsCountByGiftTable,
  listWithdrawals,
} from "../../../../../services/withdrawApiService";
import { sumGiftsByGiftTableID } from "../../../../../services/giftApiService";
import linkIcon from "../../../../../assets/icons/link.svg";
import editIcon from "../../../../../assets/icons/edit.svg";
import placeHolderImage from "../../../../../assets/placeholders/picture.jpg";
import gsap from "gsap";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../../../hoc/withTranslations/withTranslations";

const LOCALE =
  "modules_MyGiftTable_components_WithdrawSection_mobile_MobileWithdrawSection";

const MobileWithdrawSection: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info";
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const [completedWithdrawTotal, setCompletedWithdrawTotalState] = useState(0);
  const [pendingReviewWithdrawTotal, setPendingReviewWithdrawTotalState] =
    useState(0);
  const [hasPendingReview, setHasPendingReviewState] = useState(false);
  const [collectedSum, setCollectedSum] = useState(0);
  const [giftCount, setGiftCount] = useState<number>(0);
  const [withdrawalCount, setWithdrawalCount] = useState(0); // New state

  let lastScrollY = 0;
  let timeoutId: NodeJS.Timeout | null = null;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const totalValue = useAppSelector((state) => state.giftTable.totalValue);

  const tableID = useAppSelector((state: RootState) => state.giftTable.ID);
  const status = useAppSelector((state: RootState) => state.giftTable.status);
  const {
    eventName,
    eventDate,
    eventType,
    theme,
    eventLocation,
    profilePicture,
    products,
  } = useAppSelector((state: RootState) => state.giftTable);
  const userID = useAppSelector((state: RootState) => state.auth.user?.id);

  const physicalProductsCollectedValue =
    products
      ?.filter((product) => product.isPhysical && product.taken > 0)
      .reduce((sum, product) => sum + product.value * product.taken, 0) || 0;

  const collectedMoney = collectedSum - physicalProductsCollectedValue;

  useEffect(() => {
    const fetchWithdrawalCount = async () => {
      if (tableID) {
        try {
          const countResponse =
            await getSuccessfulWithdrawalsCountByGiftTable(tableID);
          setWithdrawalCount(countResponse.count);
        } catch (error) {
          console.error("Error fetching withdrawal count", error);
          setWithdrawalCount(0);
        }
      }
    };

    fetchWithdrawalCount();
  }, [tableID]);

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (profilePicture) {
        try {
          const media = await generateGetURL(profilePicture);
          setProfileImageUrl(media.signedURL);
        } catch (error) {
          console.error("Error fetching profile picture:", error);
          setProfileImageUrl(null);
        }
      }
    };
    fetchProfileImage();
  }, [profilePicture]);

  useEffect(() => {
    if (tableID) {
      sumGiftsByGiftTableID(tableID)
        .then((result) => {
          setCollectedSum(result.sum);
          setGiftCount(result.count);
        })
        .catch((error) => {
          console.error("Error fetching collected sum", error);
          setCollectedSum(0);
          setGiftCount(0);
        });
    }
  }, [tableID]);

  const handleScroll = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (!isExpanded) {
      timeoutId = setTimeout(() => {
        const currentScrollY = window.scrollY;
        setIsVisible(
          currentScrollY <= lastScrollY ||
            currentScrollY + window.innerHeight >= document.body.scrollHeight
        );
        lastScrollY = currentScrollY;
      }, 100);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isExpanded]);

  useEffect(() => {
    const fetchWithdrawals = async () => {
      try {
        const withdrawals = await listWithdrawals(
          userID as string,
          undefined,
          tableID
        );
        const completedSum = withdrawals
          .filter((w) => w.status === "payment_completed")
          .reduce((sum, w) => sum + w.amount, 0);
        const pendingReviewSum = withdrawals
          .filter((w) => w.status === "pending_review")
          .reduce((sum, w) => sum + w.amount, 0);
        const hasPending = withdrawals.some(
          (w) => w.status === "pending_review"
        );

        setCompletedWithdrawTotalState(completedSum);
        setPendingReviewWithdrawTotalState(pendingReviewSum);
        setHasPendingReviewState(hasPending);

        dispatch(setCompletedWithdrawTotal(completedSum));
        dispatch(setPendingReviewWithdrawTotal(pendingReviewSum));
        dispatch(setHasPendingReview(hasPending));
      } catch (error) {
        console.error("Error fetching withdrawals", error);
      }
    };

    fetchWithdrawals();
  }, [dispatch, tableID, userID]);

  const handleShareTable = async () => {
    try {
      await dispatch(fetchGiftTableStatus(tableID));
      if (status === "draft") {
        navigate("/my-table/choose-plan");
      } else {
        navigate("/my-table/share");
      }
    } catch (error) {
      console.error("Error handling share table", error);
    }
  };

  const handleEditClick = () => {
    navigate("/my-table/edit");
  };

  const handleViewGifts = () => {
    navigate("/my-table/gifts");
  };

  const toggleExpand = () => {
    if (isExpanded) {
      gsap.to(".withdraw-expanded-content", {
        height: 0,
        opacity: 0,
        duration: 1,
      });
    } else {
      gsap.to(".withdraw-expanded-content", {
        height: "auto",
        opacity: 1,
        duration: 1,
      });
    }
    setIsExpanded(!isExpanded);
  };

  const getWithdrawButtonState = () => {
    if (hasPendingReview) {
      return {
        disabled: true,
        altText: `${t("withdrawInProgress")} ${pendingReviewWithdrawTotal?.toLocaleString(
          "es-MX",
          {
            style: "currency",
            currency: "MXN",
          }
        )} ${t("withdrawInProgressEnd")}`,
      };
    } else if (status === "draft") {
      return {
        disabled: true,
        altText: t("tableNotActive"),
      };
    } else if (totalValue === 0) {
      return {
        disabled: true,
        altText: t("tableValueZero"),
      };
    } else if (collectedSum === 0) {
      return {
        disabled: true,
        altText: t("noFundsCollected"),
      };
    } else {
      return {
        disabled: false,
        altText: "",
      };
    }
  };

  const { disabled, altText } = getWithdrawButtonState();

  const handleWithdrawClick = () => {
    if (withdrawalCount === 0) {
      setIsModalOpen(true);
    } else {
      navigate("/my-table/withdraw/step1");
    }
  };

  const shouldHighlightShareButton = (message: string) => {
    return (
      message.includes(t("tableNotActive")) ||
      message.includes(t("noFundsCollected"))
    );
  };

  const showAlert = (message: React.ReactNode) => {
    setAlert({ message, level: "info" });
    if (typeof message === "string" && shouldHighlightShareButton(message)) {
      dispatch(
        setEvent({
          event: "myGiftTable-withDrawSection-highlightShareButton",
          value: true,
        })
      );
    } else {
      dispatch(
        setEvent({
          event: "myGiftTable-withDrawSection-highlightShareButton",
          value: false,
        })
      );
    }
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
    setTimeout(() => {
      setAlert(null);
      dispatch(
        setEvent({
          event: "myGiftTable-withDrawSection-highlightShareButton",
          value: false,
        })
      );
    }, 4000);
  };

  const handleButtonHover = () => {
    if (disabled && altText) {
      showAlert(altText);
    }
  };

  return (
    <>
      {alert && (
        <div ref={alertRef} className="fixed top-4 left-0 right-0 z-50">
          <Alert message={alert.message} level={alert.level} />
        </div>
      )}

      {/* Fixed bottom bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-gray-100 p-4 text-left z-50 rounded-t-lg shadow-xs transition-transform duration-300 ${
          isVisible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {status === "draft" ? (
          // Draft state: show info message, table value, and Publish Now button
          <div className="flex flex-col space-y-2">
            <div className="flex justify-between items-center p-2">
              <span>{t("valueOfTable")}</span>
              <span className="text-gray-900 font-bold">
                {totalValue?.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                }) || "0.00"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-yellow-600 font-bold">
                {t("tableNotActive")}
              </span>
              <Button
                onClick={() => navigate("/my-table/choose-plan")}
                type="primary"
                className="ml-4"
              >
                {t("publishNow")}
              </Button>
            </div>
          </div>
        ) : (
          // Full bar for published table
          <>
            {/* Toggle controls */}
            {!isExpanded && (
              <div className="flex justify-center items-center">
                <button
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  onClick={toggleExpand}
                >
                  {t("viewMore")}
                </button>
              </div>
            )}
            {isExpanded && (
              <div className="flex justify-center items-center">
                <button
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  onClick={toggleExpand}
                >
                  {t("viewLess")}
                </button>
              </div>
            )}

            {/* Main bar content */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700">{t("valueOfTable")}:</span>
              <span className="text-gray-900 font-bold">
                {totalValue?.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                }) || "0.00"}
              </span>
            </div>

            {isExpanded && (
              <>
                <div className="flex justify-between items-center pt-4">
                  <div>
                    <span className="text-gray-700 font-bold text-primary">
                      {t("collected")}:
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-gray-700">{t("money")}:</span>
                    <span className="text-gray-900 font-bold ml-2 text-primary">
                      {collectedMoney?.toLocaleString("es-MX", {
                        style: "currency",
                        currency: "MXN",
                      }) || "0.00"}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-gray-700">
                      {t("physicalProducts")}:
                    </span>
                    <span className="text-gray-900 font-bold ml-2 text-primary">
                      {physicalProductsCollectedValue?.toLocaleString("es-MX", {
                        style: "currency",
                        currency: "MXN",
                      }) || "0.00"}
                    </span>
                  </div>
                </div>
                {completedWithdrawTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-gray-700">{t("withdrawn")}:</span>
                      <span className="text-green-600 font-bold ml-2">
                        {completedWithdrawTotal.toLocaleString("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        })}
                      </span>
                    </div>
                  </div>
                )}
                {pendingReviewWithdrawTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-yellow-600">
                        {t("withdrawInProgress")}:
                      </span>
                      <span className="text-yellow-600 font-bold ml-2">
                        {pendingReviewWithdrawTotal.toLocaleString("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Action buttons */}
            <div className="flex justify-between items-center mt-4 gap-2">
              <Button
                onClick={disabled ? handleButtonHover : handleWithdrawClick}
                onMouseEnter={handleButtonHover}
                type="primary"
                disabled={disabled}
                className={`flex flex-col justify-center items-center h-16 w-1/3 ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <GiReceiveMoney className="h-7 w-7" />
                <span className="text-xs mt-1">{t("withdrawFunds")}</span>
              </Button>
              <Button
                onClick={handleViewGifts}
                type="secondary"
                className="flex flex-col justify-center items-center h-16 w-1/3"
              >
                <FaGift className="h-7 w-7" />
                <span className="text-xs mt-1">
                  {t("giftCount", { count: giftCount })}
                </span>
              </Button>
              <Button
                onClick={handleShareTable}
                type="secondary"
                className="flex flex-col justify-center items-center h-16 w-1/3"
              >
                <img src={linkIcon} alt={t("share")} className="h-7 w-7" />
                <span className="text-xs mt-1">{t("share")}</span>
              </Button>
            </div>

            {/* Expanded content (initially hidden) */}
            <div
              className="withdraw-expanded-content overflow-hidden"
              style={{ height: 0, opacity: 0 }}
            >
              <div className="relative flex justify-center mt-4">
                <img
                  src={profileImageUrl || placeHolderImage}
                  alt={t("eventImage") || "Event"}
                  className="w-36 h-36 rounded-full object-cover"
                />
                <div
                  className="absolute top-0 right-0 p-1 rounded-full cursor-pointer"
                  onClick={handleEditClick}
                >
                  <img src={editIcon} alt={t("edit")} className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-6">
                <p className="text-xs text-primary">{t("eventName")}:</p>
                <h4 className="text-sm font-bold text-gray-900">
                  {eventName || "N/A"}
                </h4>
              </div>
              <div className="mt-4">
                <p className="text-xs text-primary">{t("eventDate")}:</p>
                <h4 className="text-sm font-bold text-gray-900">
                  {eventDate || "N/A"}
                </h4>
              </div>
              <div className="mt-4">
                <p className="text-xs text-primary">{t("eventCategory")}:</p>
                <h4 className="text-sm font-bold text-gray-900">
                  {eventType || "N/A"}
                </h4>
              </div>
              <div className="mt-4">
                <p className="text-xs text-primary">{t("eventTheme")}:</p>
                <h4 className="text-sm font-bold text-gray-900">
                  {theme || "N/A"}
                </h4>
              </div>
              <div className="mt-4">
                <p className="text-xs text-primary">{t("eventLocation")}:</p>
                <h4 className="text-sm font-bold text-gray-900">
                  {eventLocation.address || "N/A"}
                </h4>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal className="z-50" isOpen={isModalOpen}>
        <div className="text-center">
          <h2 className="text-lg font-bold mb-4">{t("howToWithdraw")}</h2>
          <img
            src={profileImageUrl || placeHolderImage}
            alt={t("eventImage") || "Event"}
            className="w-64 h-64 rounded-full object-cover mx-auto mb-4"
          />
          <p className="text-gray-700 mb-6">{t("withdrawInstructions")}</p>
          <Button
            label={t("startWithdraw")}
            onClick={() => navigate("/my-table/withdraw/step1")}
            type="primary"
            className="w-full mb-4"
          />
          <Button
            label={t("withdrawLater")}
            onClick={() => setIsModalOpen(false)}
            type="secondary"
            className="w-full"
          />
        </div>
      </Modal>
    </>
  );
};

export default withTranslations(LOCALE)(MobileWithdrawSection);
